import { useCallback, useState } from 'react';

/**
 * Persist a piece of state to localStorage.
 *
 * Daffy-local stand-in for the Frontend-Platform `useLocalStorage` hook; instead of a
 * zod `validationSchema`, pass an optional `isValid` guard to reject malformed stored
 * values (falling back to `defaultValue`).
 */
export const useLocalStorage = <T>(
  key: string,
  defaultValue: T,
  options?: {
    /**
     * Guard against malformed stored values; when it returns false the
     * `defaultValue` is used instead.
     */
    isValid?: (value: unknown) => boolean;
  },
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => {
    try {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return defaultValue;

      const parsed = JSON.parse(stored);
      if (options?.isValid && !options.isValid(parsed)) return defaultValue;

      return parsed;
    } catch {
      return defaultValue;
    }
  });

  const setStoredValue = useCallback<React.Dispatch<React.SetStateAction<T>>>(
    next => {
      setValue(prev => {
        const resolved = next instanceof Function ? next(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          // Storage unavailable (private browsing, quota) — state still updates in-memory
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, setStoredValue];
};
